import { Component, inject, PLATFORM_ID, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../api.service';
import { TAGPermission } from '../../../models/tagpermission';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { TAGUser } from '../../../models/taguser';
import { APIError } from '../../../models/apierror';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslatePipe } from '@ngx-translate/core';
import { FluidModule } from 'primeng/fluid';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { isPlatformServer } from '@angular/common';
import { MatIcon } from '../../components/mat-icon/mat-icon';

export interface OAuthInteraction {
  client_name: string;
  scope: string;
  permission: string;
  prompt: string;
}

@Component({
  selector: 'app-oauth',
  imports: [
    ProgressSpinnerModule,
    TranslatePipe,
    FluidModule,
    FloatLabelModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    MessageModule,
    ButtonModule,
    MatIcon,
  ],
  templateUrl: './oauth.html',
  styleUrl: './oauth.scss',
})
export class Oauth {
  //private dialogService = inject(DialogService);
  //private translate = inject(TranslateService);
  //private messageService = inject(MessageService);
  //private router = inject(Router);
  private route = inject(ActivatedRoute);
  //private socket = inject(Socket);
  public api = inject(ApiService);
  private platformId = inject(PLATFORM_ID);

  uid: string;
  error = signal<string>(undefined);

  requiredPermission: TAGPermission = null;
  clientName: string = '';

  loadingInfo = signal(true);

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });
  loggingIn = signal(false);
  loginError = signal<string>(undefined);

  ngOnInit() {
    if (isPlatformServer(this.platformId)) return;

    this.api
      .waitForAPI()
      .then(() => {
        this.route.queryParams.subscribe((params) => {
          this.uid = params['uid'];

          if (this.uid == undefined) {
            this.error.set('uid is missing');
            this.loadingInfo.set(false);
            return;
          }

          // The API validated the authorization request before redirecting here
          this.api
            .getOAuthInteraction(this.uid)
            .then((result) => {
              this.requiredPermission = TAGPermission.fromName(result.permission);
              this.clientName = result.client_name;

              this.loadingInfo.set(false);
            })
            .catch((reason: APIError) => {
              this.error.set(reason.message);
              this.loadingInfo.set(false);
            });
        });
      })
      .catch((reason) => {
        this.error.set(reason?.message ?? reason);
        this.loadingInfo.set(false);
      });
  }

  login() {
    if (!this.loginForm.valid) return;

    this.loggingIn.set(true);
    this.loginError.set('');

    this.api
      .login(this.loginForm.value.username, this.loginForm.value.password)
      .then((result: TAGUser) => {
        this.loggingIn.set(false);
        this.loginError.set('');
      })
      .catch((reason: APIError) => {
        this.loggingIn.set(false);
        if (reason.error == 2) {
          this.loginError.set('general.login.error.invalid');
        } else if (reason.error == 3 || reason.error == 7) {
          this.loginError.set('general.login.error.wronguser');
        } else {
          this.loginError.set('An unknown error occured.');
        }
      });
  }

  logout() {
    this.api.logout();
  }

  // Only decides what the page shows, the API checks the permission again on accept
  hasRequiredPermission() {
    return this.requiredPermission != undefined && this.api.hasPermission(this.requiredPermission);
  }

  decline() {
    this.api
      .declineOAuthInteraction(this.uid)
      .then((result) => {
        window.location.href = result;
      })
      .catch((reason: APIError) => {
        this.error.set(reason.message);
      });
  }

  accept() {
    this.api
      .acceptOAuthInteraction(this.uid)
      .then((result) => {
        window.location.href = result;
      })
      .catch((reason: APIError) => {
        this.error.set(reason.message);
      });
  }

  goBack() {
    window.history.back();
  }
}
